import {
	KeyOutlined,
	MailOutlined,
	PhoneOutlined,
	QuestionMarkOutlined,
	VisibilityOffOutlined,
	VisibilityOutlined,
} from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	Link,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { AuthenticatorKey, IdxTransaction, NextStep, Tokens } from '@okta/okta-auth-js';
import { NextStepWithType } from './oktaTypes';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import { useAppLang } from '../../../../app/features/lang/providers/AppLangProvider';
import AppLoaderContent from '../../../../app/features/loader/content/AppLoaderContent';
import useAuthOktaUser from '../../../../app/hooks/useAuthOktaUser';
import styleVariables from '../../../../app/styles/styleVariables';
import langAuthOktaIdx from './langs/langAuthOktaIdx';
import { langLookup } from '../../../../app/utils/langLookup';
import { sanitizeHtml } from '../../../../app/utils/sanitizeHtml';
import AuthCode from 'react-auth-code-input';
import { useNavigate } from 'react-router-dom';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import AuthRoutes from '../../AuthRoutes';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import useAuthOktaUserActivationSend from '../user-activation/hooks/useAuthOktaUserActivationSend';
import { globalConfig } from '../../../../app/config/config';
import useAppLog from '../../../../app/features/log/hooks/useAppLog';
type FAuthOktaIdxProps = {
	username: string;
	onSuccess: (tokens: Tokens) => void;
	onCancel?: () => void;
};

interface IUserProfile {
	firstName?: string;
	lastName?: string;
	locale?: string;
	timeZone?: string;
}

const FAuthOktaIdx = (props: FAuthOktaIdxProps) => {
	const { username, onSuccess, onCancel } = props;
	const authOktaUser = useAuthOktaUser();

	const navigate = useNavigate();
	const { t } = useTranslation();
	const appLog = useAppLog();

	const appLang = useAppLang();

	const theme = useTheme();
	const { mutate: userActivateSend } = useAuthOktaUserActivationSend();

	const identifier = username;
	const [availableSteps, setAvailableSteps] = useState<NextStep[] | null>(null);
	const [nextStep, setNextStep] = useState<NextStep | null>(null);
	const [status, setStatus] = useState<string | null>(null);
	const [userProfile, setUserProfile] = useState<IUserProfile | null>(null);
	const [currentTransaction, setCurrentTransaction] = useState<IdxTransaction | null>(null);
	const [errorActivation, setErrorActivation] = useState(false);
	const [resendActivation, setResendActivation] = useState(false);
	const [expiredAt, setExpiredAt] = useState<Date | null>(null);

	const [nbVerificationSuccess, setNbVerificationSuccess] = useState<number>(0);

	useEffect(() => {
		if (currentTransaction && currentTransaction.rawIdxState) {
			setExpiredAt(moment(currentTransaction.rawIdxState.expiresAt).toDate());
		}
	}, [currentTransaction]);

	// Cancels the running transaction once `expiredAt` is reached so the user
	// can't keep submitting against a stale Okta session handle.
	useEffect(() => {
		const interval = setInterval(() => {
			if (expiredAt) {
				const diffTime = moment(expiredAt).diff(moment(), 's');
				if (diffTime <= 0) {
					onCancel?.();
				}
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [expiredAt, onCancel]);

	const showLoginError = () => {
		setResendActivation(false);
		setErrorActivation(true);
	};

	// The backend answers the same for any account (ITP-11640): send the
	// activation mail and show a neutral message.
	const handleErrorActivation = () => {
		userActivateSend(
			{ login: identifier, locale: appLang.language },
			{
				onSuccess: () => {
					setResendActivation(true);
				},
				onError: () => {
					appLog.warning(
						`FAuthOktaIdx/handleErrorActivation: error when sending activation mail to ${identifier}`,
					);
					showLoginError();
				},
			},
		);
	};

	useEffect(() => {
		if (identifier) {
			setUserProfile(null);
			authOktaUser.storageManager.getTransactionStorage().clearStorage();
			authOktaUser.idx.clearTransactionMeta();
			authOktaUser.idx.startTransaction();
			authOktaUser.idx
				.authenticate({
					username: identifier,
					authenticator: AuthenticatorKey.OKTA_PASSWORD,
				})
				.then((transaction) => {
					if (transaction.requestDidSucceed) {
						handleChangeTransaction(transaction);
					} else {
						handleErrorActivation();
						appLog.warning('FAuthOktaIdx/authenticate: transaction.requestDidSucceed == False');
					}
				})
				.catch((e) => {
					// Rejected identifiers resolve with requestDidSucceed=false (handled
					// above); a rejection is a network / SDK / Okta error: show the
					// login error, no activation mail. The SDK exposes the HTTP status
					// on `xhr`, not at the top level.
					const httpStatus = (e as { xhr?: { status?: number } }).xhr?.status;
					appLog.error('FAuthOktaIdx/authenticate: error' + (httpStatus ? ' status:' + httpStatus : ''));
					showLoginError();
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [identifier]);

	const handleChangeTransaction = (
		transaction: IdxTransaction | null,
		state?: 'challenge_authenticator_success' | null,
	) => {
		const rawState = transaction?.rawIdxState as { failure?: { name?: string } } | undefined;
		// `failure-redirect` is Okta's signal that the user has no access to the
		// application: an activation mail would not help, show the login error.
		if (transaction && transaction.requestDidSucceed && rawState?.failure?.name === 'failure-redirect') {
			showLoginError();
		}
		if (state === 'challenge_authenticator_success' && transaction && transaction.requestDidSucceed) {
			setNbVerificationSuccess((prevData) => prevData + 1);
		}

		setCurrentTransaction(transaction);
	};

	useEffect(() => {
		if (currentTransaction) {
			setStatus(currentTransaction.status);
			setAvailableSteps(currentTransaction.availableSteps ?? null);
			setNextStep(currentTransaction.nextStep ?? null);
			const userValue = currentTransaction.context?.user?.value as { profile?: IUserProfile } | undefined;
			setUserProfile(userValue?.profile ?? null);
		} else {
			setStatus(null);
			setAvailableSteps(null);
			setNextStep(null);
			setUserProfile(null);
		}
	}, [currentTransaction]);

	useEffect(() => {
		if (
			userProfile &&
			appLang.language &&
			userProfile.locale &&
			userProfile.locale.slice(0, 2) !== appLang.language
		) {
			appLang.changeLanguage(userProfile.locale.slice(0, 2));
		}
	}, [userProfile, appLang]);

	useEffect(() => {
		if (currentTransaction && status === 'SUCCESS' && currentTransaction.tokens) {
			onSuccess(currentTransaction.tokens);
		}
	}, [status, currentTransaction, onSuccess]);

	useEffect(() => {
		if (nextStep) {
			if (nextStep.name === 'cancel') {
				navigate(HAppRoutes.getPath(AuthRoutes.logout.tag));
			}
		}
	}, [navigate, nextStep]);

	const renderStep = () => {
		if (nextStep && currentTransaction) {
			switch (nextStep.name) {
				case 'identify':
				case 'cancel':
					return <Cancel step={nextStep} onChange={handleChangeTransaction} onCancel={onCancel} />;
				case 'challenge-authenticator':
					return (
						<ChallengeAuthenticator
							step={nextStep}
							currentTransaction={currentTransaction}
							onChange={handleChangeTransaction}
						/>
					);
				case 'enroll-authenticator':
					return <EnrollAuthenticator step={nextStep} onChange={handleChangeTransaction} />;
				case 'reenroll-authenticator':
					return <ReenrollAuthenticator step={nextStep} onChange={handleChangeTransaction} />;
				case 'authenticator-enrollment-data':
					return <AuthenticatorEnrollmentData step={nextStep} onChange={handleChangeTransaction} />;
				case 'authenticator-verification-data':
					return <AuthenticatorVerificationData step={nextStep} onChange={handleChangeTransaction} />;
				case 'select-authenticator-authenticate':
					return <SelectAuthenticatorAuthenticate step={nextStep} onChange={handleChangeTransaction} />;
				case 'select-authenticator-enroll':
					return <SelectAuthenticatorEnroll step={nextStep} onChange={handleChangeTransaction} />;
				default:
					appLog.warning(`${nextStep.name} non traité [FAuthOktaIdx]`);
					return <>{nextStep.name} non traité [FAuthOktaIdx]</>;
			}
		} else {
			return <AppLoaderContent sx={{ height: '200px', minHeight: '200px' }} />;
		}
	};

	const steps = [
		t(langAuthOktaIdx.step_identify.tag),
		t(langAuthOktaIdx.step_1st_check.tag),
		t(langAuthOktaIdx.step_2nd_check.tag),
	];

	return (
		<Box
			sx={{
				'& .MuiInputBase-root': {
					border: '1px solid #CCC',
				},
			}}
		>
			<Stepper sx={{ marginTop: '20px', marginBottom: '30px' }} activeStep={nbVerificationSuccess}>
				{steps.map((label, key) => (
					<Step key={key}>
						<StepLabel
							sx={{
								'& .MuiStepIcon-root': {
									color: nbVerificationSuccess + 1 === key ? theme.palette.secondary.main : '',
								},
								'& .MuiStepLabel-label': {
									color: nbVerificationSuccess + 1 !== key ? theme.palette.primary.main : '',
								},
							}}
						>
							{label}
						</StepLabel>
					</Step>
				))}
			</Stepper>
			{userProfile ? (
				<Box>
					{userProfile.firstName} {userProfile.lastName}
				</Box>
			) : (
				''
			)}
			{identifier ? <Box sx={{ color: styleVariables.greyColor }}>{identifier}</Box> : ''}

			<Divider sx={{ margin: '20px 0px' }} />
			{resendActivation ? (
				<>
					<Alert severity="info">
						<div
							dangerouslySetInnerHTML={{
								// nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- sanitized via DOMPurify; first-party i18n markup, email is self-reflected user input
								__html: sanitizeHtml(
									t(langAuthOktaIdx.error_resend_activation.tag, {
										email: identifier,
										interpolation: { escapeValue: false },
									}),
								),
							}}
						/>
					</Alert>
					<Button
						onClick={() => onCancel?.()}
						sx={{ marginTop: '30px' }}
						startIcon={<AppCustomIcon tag="arrow-left" />}
					>
						{t(langAuthOktaIdx.error_btn_back_to_login.tag)}
					</Button>
				</>
			) : errorActivation ? (
				<>
					<Alert severity="error">
						<div
							dangerouslySetInnerHTML={{
								__html: t(langAuthOktaIdx.error_cannot_login.tag, {
									interpolation: { escapeValue: false },
								}),
							}}
						/>
					</Alert>
					<Button
						onClick={() => onCancel?.()}
						sx={{ marginTop: '30px' }}
						startIcon={<AppCustomIcon tag="arrow-left" />}
					>
						{t(langAuthOktaIdx.error_btn_back_to_login.tag)}
					</Button>
				</>
			) : (
				<>
					{renderStep()}
					<Divider sx={{ margin: '20px 0px' }} />
				</>
			)}

			<AvailableSteps
				step={nextStep}
				availableSteps={
					availableSteps && nextStep
						? availableSteps.filter((x) => {
								const xType = (x as NextStepWithType).type;
								const stepType = (nextStep as NextStepWithType).type;
								return (
									(nbVerificationSuccess !== 0 ||
										(nbVerificationSuccess === 0 &&
											x.name !== 'select-authenticator-authenticate')) &&
									x.name !== nextStep.name &&
									(!xType || !stepType || (xType && xType !== stepType))
								);
						  })
						: null
				}
				onChangeNextStep={(nextStep) => setNextStep(nextStep)}
			/>
		</Box>
	);
};

type AvailableStepsProps = {
	availableSteps: NextStep[] | null;
	step: NextStep | null;
	onChangeNextStep: (availableStep: NextStep) => void;
};
const AvailableSteps = (props: AvailableStepsProps) => {
	const { step, availableSteps, onChangeNextStep } = props;
	const { t } = useTranslation();

	const [stepSelectAuthenticatorAuthenticate, setStepSelectAuthenticatorAuthenticate] = useState<NextStep | null>(
		null,
	);
	const [stepCancel, setStepCancel] = useState<NextStep | null>(null);

	useEffect(() => {
		if (availableSteps && availableSteps.some((x) => x.name === 'select-authenticator-authenticate')) {
			setStepSelectAuthenticatorAuthenticate(
				availableSteps.find((x) => x.name === 'select-authenticator-authenticate') ?? null,
			);
		} else {
			setStepSelectAuthenticatorAuthenticate(null);
		}
		if (availableSteps && availableSteps.some((x) => x.name === 'cancel')) {
			setStepCancel(availableSteps.find((x) => x.name === 'cancel') ?? null);
		} else {
			setStepCancel(null);
		}
	}, [availableSteps]);

	return (
		<>
			{availableSteps && step ? (
				<Box sx={{ textAlign: 'left', lineHeight: '2rem' }}>
					{stepSelectAuthenticatorAuthenticate ? (
						<Box>
							<Link onClick={() => onChangeNextStep(stepSelectAuthenticatorAuthenticate)}>
								{t(langAuthOktaIdx.available_steps_select_authenticator_authenticate.tag)}
							</Link>
						</Box>
					) : (
						''
					)}
					{stepCancel ? (
						<Box>
							<Link onClick={() => onChangeNextStep(stepCancel)}>
								{t(langAuthOktaIdx.available_steps_cancel.tag)}
							</Link>
						</Box>
					) : (
						''
					)}
					{/* <hr />
					{availableSteps
						.filter(
							(x) =>
								x.name !== step.name &&
								(!x['type'] || !(step as NextStepWithType).type || (x['type'] && x['type'] !== (step as NextStepWithType).type)),
						)
						.map((availableStep, index) => {
							return (
								<Box key={index} sx={{ textAlign: 'left' }}>
									<Link onClick={() => onChangeNextStep(availableStep)}>
										{availableStep.name} - {availableStep['type']}
									</Link>
								</Box>
							);
						})} */}
				</Box>
			) : (
				''
			)}
		</>
	);
};

type SelectAuthenticatorAuthenticateProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const SelectAuthenticatorAuthenticate = (props: SelectAuthenticatorAuthenticateProps) => {
	const { step, onChange } = props;

	const { t } = useTranslation();
	const authOktaUser = useAuthOktaUser();

	const handleSelect = (authenticatorKey: string) => {
		if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx
				.proceed({ authenticator: authenticatorKey })
				.then((transaction) => {
					onChange(transaction);
				})
				.catch(() => {
					// Okta IDX rejects on user-cancel — swallow so the UI doesn't crash.
				});
		}
	};

	const [stepOptions, setStepOptions] = useState<{ value: string }[] | null>(null);
	useEffect(() => {
		if (step && step.inputs) {
			const optTmp = step.inputs.find((x) => x.name === 'authenticator');
			if (optTmp) {
				setStepOptions((optTmp as { options?: { value: string }[] }).options ?? null);
			}
		}
	}, [step]);

	return (
		<>
			<Typography variant="h3" color="secondary">
				{t(langAuthOktaIdx.select_authenticator_authenticate_title.tag)}
			</Typography>
			<List>
				{step &&
					stepOptions &&
					stepOptions
						.map((option) => option.value)
						.filter((value) =>
							['okta_password', 'okta_email', 'phone_number', 'security_question'].includes(value),
						)
						.map((value: string, index: number) => {
							return (
								<ListItem
									key={index}
									disablePadding
									sx={{ backgroundColor: '#FAFAFA', marginBottom: '10px', borderRadius: '6px' }}
								>
									<ListItemButton
										onClick={() => handleSelect(value)}
										id={`select-authenticator-${value}`}
									>
										<ListItemIcon>
											{value === 'okta_email' ? (
												<MailOutlined />
											) : value === 'okta_password' ? (
												<KeyOutlined />
											) : value === 'security_question' ? (
												<QuestionMarkOutlined />
											) : (
												<PhoneOutlined />
											)}
										</ListItemIcon>
										<ListItemText
											primary={t(
												langLookup(
													langAuthOktaIdx,
													`select_authenticator_authenticate_method_${value}`,
												)?.tag ?? '',
											)}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
			</List>
		</>
	);
};

type CancelProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction | null) => void;
	onCancel?: () => void;
};
const Cancel = (props: CancelProps) => {
	const { onChange, onCancel } = props;

	const authOktaUser = useAuthOktaUser();
	useEffect(() => {
		authOktaUser.idx.cancel().then(() => {
			onChange(null);
			onCancel?.();
		});
	});
	return <></>;
};

type ChallengeAuthenticatorProps = {
	step: NextStep;
	currentTransaction: IdxTransaction;
	onChange: (transaction: IdxTransaction, state?: 'challenge_authenticator_success' | null) => void;
};
const ChallengeAuthenticator = (props: ChallengeAuthenticatorProps) => {
	const { step, currentTransaction, onChange } = props;

	const renderType = () => {
		if ((step as NextStepWithType).type) {
			switch ((step as NextStepWithType).type) {
				case 'password':
					return <ChallengeAuthenticatorPassword step={step} onChange={onChange} />;
				case 'email':
					return <ChallengeAuthenticatorEmail step={step} onChange={onChange} />;
				case 'security_question':
					return <ChallengeAuthenticatorSecurityQuestion step={step} onChange={onChange} />;
				case 'phone':
					return (
						<ChallengeAuthenticatorPhone
							step={step}
							currentTransaction={currentTransaction}
							onChange={onChange}
						/>
					);
				default:
					return <>{(step as NextStepWithType).type} non traité [ChallengeAuthenticator]</>;
			}
		} else {
			return <></>;
		}
	};

	return <>{renderType()}</>;
};

type AuthenticatorVerificationDataProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const AuthenticatorVerificationData = (props: AuthenticatorVerificationDataProps) => {
	const { step, onChange } = props;

	const appLog = useAppLog();

	const renderType = () => {
		if ((step as NextStepWithType).type) {
			switch ((step as NextStepWithType).type) {
				case 'email':
					return <AuthenticatorVerificationDataEmail step={step} onChange={onChange} />;
				case 'phone':
					return <AuthenticatorVerificationDataSms step={step} onChange={onChange} />;
				default:
					appLog.warning(`${(step as NextStepWithType).type} non traité [AuthenticatorVerificationData]`);
					return <>{(step as NextStepWithType).type} non traité [AuthenticatorVerificationData]</>;
			}
		} else {
			return <></>;
		}
	};

	return <>{renderType()}</>;
};

type AuthenticatorVerificationDataSmsProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const AuthenticatorVerificationDataSms = (props: AuthenticatorVerificationDataSmsProps) => {
	const { step, onChange } = props;
	const { t } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx.proceed({ methodType: 'sms' }).then((transaction) => {
				onChange(transaction);
			});
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h3" color="secondary">
						{t(langAuthOktaIdx.authenticator_verification_data_sms_title.tag)}
					</Typography>
					<Typography variant="body1" component="div">
						<Trans
							t={t}
							i18nKey={langAuthOktaIdx.authenticator_verification_data_sms_description.tag}
							values={{ phoneNumber: step?.authenticator?.profile?.phoneNumber }}
						/>
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.authenticator_verification_data_sms_btn_submit.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type AuthenticatorVerificationDataEmailProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const AuthenticatorVerificationDataEmail = (props: AuthenticatorVerificationDataEmailProps) => {
	const { step, onChange } = props;
	const { t } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx.proceed({ methodType: 'email' }).then((transaction) => {
				onChange(transaction);
			});
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h3" color="secondary">
						{t(langAuthOktaIdx.authenticator_verification_data_email_title.tag)}
					</Typography>
					<Typography variant="body1" component="div">
						<Trans
							t={t}
							i18nKey={langAuthOktaIdx.authenticator_verification_data_email_description.tag}
							values={{ email: step?.authenticator?.profile?.email }}
						/>
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.authenticator_verification_data_email_btn_submit.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type ChallengeAuthenticatorPhoneProps = {
	step: NextStep;
	currentTransaction: IdxTransaction;
	onChange: (transaction: IdxTransaction, state?: 'challenge_authenticator_success' | null) => void;
};
const ChallengeAuthenticatorPhone = (props: ChallengeAuthenticatorPhoneProps) => {
	const { currentTransaction, onChange } = props;
	const { t, i18n } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const [verificationCode, setVerificationCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (!verificationCode || verificationCode.length !== 6) {
			setError(t(langAuthOktaIdx.challenge_authenticator_sms_field_error_is_required.tag));
		} else if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx.proceed({ verificationCode }).then((transaction) => {
				if (transaction.requestDidSucceed === true) {
					onChange(transaction, 'challenge_authenticator_success');
				} else {
					if (
						i18n.exists(
							`auth.okta_idx.challenge_authenticator_sms_field_error.${transaction.messages?.[0].i18n.key}`,
						)
					) {
						setError(
							t(
								`auth.okta_idx.challenge_authenticator_sms_field_error.${transaction.messages?.[0].i18n.key}`,
							),
						);
					} else {
						setError(transaction.messages?.[0].i18n.key ?? null);
					}
					onChange(transaction);
				}
			});
		}
	};

	// Bypasses okta-auth-js's `currentTransaction.resend('sms')` which doesn't
	// expose the underlying error and posts directly so we can surface it.
	const handleResendSms = () => {
		const axioOkta = axios.create({
			headers: {
				'Content-Type': 'application/json',
			},
			baseURL: globalConfig.get().APP_OKTA_USER_ISSUER,
		});
		axioOkta
			.post('/idp/idx/challenge/resend', {
				stateHandle: (currentTransaction.rawIdxState as { stateHandle?: string }).stateHandle,
			})
			.then(() => {
				setDisplayResendBtn(false);
			})
			.catch((e: AxiosError) => {
				const data = e.response?.data as { messages?: { value?: string[] } } | undefined;
				setError(data?.messages?.value?.[0] ?? null);
			});
	};

	const [displayResendBtn, setDisplayResendBtn] = useState(false);
	useEffect(() => {
		const timer = setTimeout(() => {
			if (displayResendBtn === false) {
				setDisplayResendBtn(true);
			}
		}, 35000);
		return () => clearTimeout(timer);
	}, [displayResendBtn]);

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				{displayResendBtn ? (
					<Grid item xs={12}>
						<Alert severity="warning">
							{t(langAuthOktaIdx.warning_resend_text.tag)}{' '}
							<Link onClick={() => handleResendSms()} color="inherit">
								{t(langAuthOktaIdx.warning_resend_link.tag)}
							</Link>
						</Alert>
					</Grid>
				) : (
					''
				)}
				<Grid item xs={12}>
					<TextField
						variant="outlined"
						size="medium"
						label={t(langAuthOktaIdx.challenge_authenticator_sms_field_label_code.tag)}
						fullWidth
						autoComplete="off"
						type="text"
						value={verificationCode ?? ''}
						error={!!error}
						helperText={error ?? ''}
						onChange={(e) => {
							setVerificationCode(e.target.value);
						}}
					/>
				</Grid>

				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.challenge_authenticator_sms_btn_submit.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type ChallengeAuthenticatorEmailProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction, state?: 'challenge_authenticator_success' | null) => void;
};
const ChallengeAuthenticatorEmail = (props: ChallengeAuthenticatorEmailProps) => {
	const { step, onChange } = props;
	const { t, i18n } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const [verificationCode, setVerificationCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (!verificationCode || verificationCode.length !== 6) {
			setError(t(langAuthOktaIdx.challenge_authenticator_email_field_verification_code_is_invalid.tag));
		} else if (authOktaUser.idx.canProceed()) {
			setIsSubmitting(true);
			authOktaUser.idx
				.proceed({ verificationCode })
				.then((transaction) => {
					if (transaction.requestDidSucceed) {
						onChange(transaction, 'challenge_authenticator_success');
					} else {
						if (
							i18n.exists(
								`auth.okta_idx.challenge_authenticator_email_field_error.${transaction.messages?.[0].i18n.key}`,
							)
						) {
							setError(
								t(
									`auth.okta_idx.challenge_authenticator_email_field_error.${transaction.messages?.[0].i18n.key}`,
								),
							);
						} else {
							setError(transaction.messages?.[0].i18n.key ?? null);
						}
						onChange(transaction);
					}
				})
				.catch(() => {
					// Okta IDX rejects on user-cancel — swallow so the UI doesn't crash.
				})
				.finally(() => {
					setIsSubmitting(false);
				});
		}
	};

	const formRef = useRef<HTMLFormElement>(null);

	useEffect(() => {
		if (verificationCode && verificationCode.length === 6 && formRef.current) {
			if (typeof formRef.current.requestSubmit === 'function') {
				formRef.current.requestSubmit();
			} else {
				formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
			}
		}
	}, [verificationCode]);

	return (
		<form ref={formRef} onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h3" color="secondary">
						{t(langAuthOktaIdx.challenge_authenticator_email_title.tag)}
					</Typography>
					<Typography variant="body1" component="div">
						<Trans
							t={t}
							i18nKey={langAuthOktaIdx.challenge_authenticator_email_description.tag}
							values={{ email: step?.authenticator?.profile?.email }}
						/>
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							'& .otp-container': {
								marginBottom: '30px',
							},
							'& .otp-input': {
								width: '60px',
								height: '60px',
								padding: 0,
								fontSize: '24px',
								color: 'white',
								border: '1px solid ' + styleVariables.primaryColor,
								textAlign: 'center',
								marginRight: '12px',
								textTransform: 'uppercase',
								borderRadius: '4px',
								background: styleVariables.primaryColor,
								backgroundClip: 'padding-box',
							},
						}}
					>
						<AuthCode
							containerClassName="otp-container"
							inputClassName="otp-input"
							autoFocus
							onChange={(value) => setVerificationCode(value)}
						/>
					</Box>
					{error ? (
						<Alert severity="error" sx={{ boxShadow: 'none' }} variant="outlined">
							{error}
						</Alert>
					) : (
						''
					)}
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							disabled={isSubmitting}
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.challenge_authenticator_email_btn_submit.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type ChallengeAuthenticatorPasswordProps = {
	step?: NextStep;
	onChange: (transaction: IdxTransaction, state?: 'challenge_authenticator_success' | null) => void;
};
const ChallengeAuthenticatorPassword = (props: ChallengeAuthenticatorPasswordProps) => {
	const { onChange } = props;
	const { t, i18n } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const [password, setPassword] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (!password || password.length < 8) {
			setError(t(langAuthOktaIdx.challenge_authenticator_password_field_error_is_required.tag));
		} else if (authOktaUser.idx.canProceed()) {
			setIsSubmitting(true);
			authOktaUser.idx
				.proceed({ password })
				.then((transaction) => {
					if (transaction.requestDidSucceed === true) {
						onChange(transaction, 'challenge_authenticator_success');
					} else {
						if (
							i18n.exists(
								`auth.okta_idx.challenge_authenticator_password_field_error.${transaction.messages?.[0].i18n.key}`,
							)
						) {
							setError(
								t(
									`auth.okta_idx.challenge_authenticator_password_field_error.${transaction.messages?.[0].i18n.key}`,
								),
							);
						} else {
							setError(transaction.messages?.[0].i18n.key ?? null);
						}
						onChange(transaction);
					}
				})
				.catch(() => {
					// Okta IDX rejects on user-cancel — swallow so the UI doesn't crash.
				})
				.finally(() => {
					setIsSubmitting(false);
				});
		}
	};

	const [displayPassword, setDisplayPassword] = useState(false);

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h3" color="secondary">
						{t(langAuthOktaIdx.challenge_authenticator_password_title.tag)}
					</Typography>
					<Typography variant="body1">
						{t(langAuthOktaIdx.challenge_authenticator_password_description.tag)}
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<TextField
						variant="outlined"
						size="medium"
						name="password"
						label={t(langAuthOktaIdx.challenge_authenticator_password_field_label_password.tag)}
						fullWidth
						autoComplete="off"
						type={displayPassword ? 'text' : 'password'}
						value={password ?? ''}
						error={!!error}
						helperText={error ?? ''}
						onChange={(e) => {
							setPassword(e.target.value);
						}}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setDisplayPassword(!displayPassword)}>
										{displayPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
									</IconButton>
									<AppCustomIcon tag="auth_password" sx={{ color: styleVariables.primaryColor }} />
								</InputAdornment>
							),
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							id="password-submit"
							disabled={isSubmitting}
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.challenge_authenticator_password_btn_submit.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type ChallengeAuthenticatorSecurityQuestionProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction, state?: 'challenge_authenticator_success' | null) => void;
};
const ChallengeAuthenticatorSecurityQuestion = (props: ChallengeAuthenticatorSecurityQuestionProps) => {
	const { step, onChange } = props;
	const { t, i18n } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const [stepAuthenticatorProfile, setStepAuthenticatorProfile] = useState<{ questionKey?: string } | null>(null);
	const [answer, setAnswer] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (step && step.authenticator) {
			setStepAuthenticatorProfile((step.authenticator.profile as { questionKey?: string }) ?? null);
		}
	}, [step]);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (!answer || answer === '') {
			setError(t(langAuthOktaIdx.challenge_authenticator_security_question_field_anwser_is_required.tag));
		} else if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx.proceed({ answer }).then((transaction) => {
				if (transaction.requestDidSucceed) {
					onChange(transaction, 'challenge_authenticator_success');
				} else {
					if (
						i18n.exists(
							`auth.okta_idx.challenge_authenticator_security_question_field_anwser_error.${transaction.messages?.[0].i18n.key}`,
						)
					) {
						setError(
							t(
								`auth.okta_idx.challenge_authenticator_security_question_field_anwser_error.${transaction.messages?.[0].i18n.key}`,
							),
						);
					} else {
						setError(transaction.messages?.[0].i18n.key ?? null);
					}
					onChange(transaction);
				}
			});
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h3" color="secondary">
						{t(langAuthOktaIdx.challenge_authenticator_security_question_title.tag)}
					</Typography>
					<Typography variant="body1">
						{t(langAuthOktaIdx.challenge_authenticator_security_question_description.tag)}
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Box sx={{ textAlign: 'center', fontWeight: 600 }}>
						{stepAuthenticatorProfile?.questionKey &&
						i18n.exists(
							`auth.okta_factor_question.question_option_label_${stepAuthenticatorProfile.questionKey}`,
						)
							? t(
									`auth.okta_factor_question.question_option_label_${stepAuthenticatorProfile.questionKey}`,
							  )
							: ''}
					</Box>
				</Grid>
				<Grid item xs={12}>
					<TextField
						variant="outlined"
						size="medium"
						name="answer"
						label={t(langAuthOktaIdx.challenge_authenticator_security_question_field_label_answer.tag)}
						fullWidth
						autoComplete="off"
						type="password"
						value={answer ?? ''}
						error={!!error}
						helperText={error ?? ''}
						onChange={(e) => {
							setAnswer(e.target.value);
							setError(null);
						}}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<QuestionMarkOutlined />
								</InputAdornment>
							),
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							id="answer-submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.challenge_authenticator_security_question_btn_submit.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type AuthenticatorEnrollmentDataProps = {
	step?: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const AuthenticatorEnrollmentData = (props: AuthenticatorEnrollmentDataProps) => {
	const { onChange } = props;

	const authOktaUser = useAuthOktaUser();

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx.proceed({ methodType: 'sms' }).then((transaction) => {
				onChange(transaction);
			});
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12} />
				<Grid item xs={12}>
					AuthenticatorEnrollmentData
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							SEND
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type SelectAuthenticatorEnrollProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const SelectAuthenticatorEnroll = (props: SelectAuthenticatorEnrollProps) => {
	const { step, onChange } = props;

	const authOktaUser = useAuthOktaUser();

	const handleSelect = (authenticatorKey: string) => {
		if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx
				.proceed({ authenticator: authenticatorKey })
				.then((transaction) => {
					onChange(transaction);
				})
				.catch(() => {
					// Okta IDX rejects on user-cancel — swallow so the UI doesn't crash.
				});
		}
	};

	const stepOptions: { value: string; label: string }[] = [];

	return (
		<>
			{step &&
				stepOptions &&
				stepOptions.map((option, index) => {
					return (
						<Box key={index} onClick={() => handleSelect(option.value)}>
							Set up - TODO {option.label}
						</Box>
					);
				})}
		</>
	);
};

type EnrollAuthenticatorProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const EnrollAuthenticator = (props: EnrollAuthenticatorProps) => {
	const { step, onChange } = props;

	const appLog = useAppLog();

	const renderType = () => {
		if ((step as NextStepWithType).type) {
			switch ((step as NextStepWithType).type) {
				case 'security_question':
					return <EnrollAuthenticatorSecurityQuestion step={step} onChange={onChange} />;
				default:
					appLog.warning(`${(step as NextStepWithType).type} non traité [EnrollAuthenticator]`);
					return <>{(step as NextStepWithType).type} non traité [EnrollAuthenticator]</>;
			}
		} else {
			return <></>;
		}
	};

	return <>{renderType()}</>;
};

type ReenrollAuthenticatorProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const ReenrollAuthenticator = (props: ReenrollAuthenticatorProps) => {
	const { step, onChange } = props;

	const appLog = useAppLog();

	const renderType = () => {
		if ((step as NextStepWithType).type) {
			switch ((step as NextStepWithType).type) {
				case 'password':
					return <ReenrollAuthenticatorPassword step={step} onChange={onChange} />;
				default:
					appLog.warning(`${(step as NextStepWithType).type} non traité [ReenrollAuthenticator]`);
					return <>{(step as NextStepWithType).type} non traité [ReenrollAuthenticator]</>;
			}
		} else {
			return <></>;
		}
	};

	return <>{renderType()}</>;
};

type EnrollAuthenticatorSecurityQuestionProps = {
	step: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const EnrollAuthenticatorSecurityQuestion = (props: EnrollAuthenticatorSecurityQuestionProps) => {
	const { step, onChange } = props;
	const { t } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const [questionKeys, setQuestionKeys] = useState<string[] | null>(null);
	const [questionKey, setQuestionKey] = useState<string>('');
	const [answer, setAnswer] = useState<string | null>(null);

	useEffect(() => {
		if (step && step.authenticator && step.authenticator.contextualData) {
			setQuestionKeys((step.authenticator.contextualData as { questionKeys?: string[] }).questionKeys ?? null);
		}
	}, [step]);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (authOktaUser.idx.canProceed()) {
			authOktaUser.idx.proceed({ questionKey, answer: answer ?? undefined }).then((transaction) => {
				onChange(transaction);
			});
		}
		alert(answer);
	};

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				{questionKeys ? (
					<Grid item xs={12}>
						<Select
							value={questionKey}
							label="Sélectionnez une question"
							onChange={(e) => {
								setQuestionKey(e.target.value);
							}}
						>
							{questionKeys.map((questionKey: string) => {
								return (
									<MenuItem key={questionKey} value={questionKey}>
										{questionKey}
									</MenuItem>
								);
							})}
						</Select>
					</Grid>
				) : (
					''
				)}
				<Grid item xs={12}>
					<TextField
						variant="outlined"
						size="medium"
						label="Réponse à la question secrète"
						fullWidth
						autoComplete="off"
						type="text"
						value={answer ?? ''}
						onChange={(e) => {
							setAnswer(e.target.value);
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.btn_save.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

type ReenrollAuthenticatorPasswordProps = {
	step?: NextStep;
	onChange: (transaction: IdxTransaction) => void;
};
const ReenrollAuthenticatorPassword = (props: ReenrollAuthenticatorPasswordProps) => {
	const { onChange } = props;
	const { t } = useTranslation();

	const authOktaUser = useAuthOktaUser();

	const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
	const [newPassword, setNewPassword] = useState<string | null>(null);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (authOktaUser.idx.canProceed() && newPassword) {
			authOktaUser.idx.proceed({ newPassword }).then((transaction) => {
				if (transaction.requestDidSucceed === false) {
					setNewPasswordError(transaction.messages?.[0].message ?? null);
				} else {
					onChange(transaction);
				}
			});
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography variant="h3" color="secondary">
						{t(langAuthOktaIdx.reenroll_authenticator_password_title.tag)}
					</Typography>
					<Typography variant="body1">
						{t(langAuthOktaIdx.reenroll_authenticator_password_description.tag)}
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<TextField
						variant="outlined"
						size="medium"
						label={t(langAuthOktaIdx.reenroll_authenticator_password_label_new_password.tag)}
						fullWidth
						autoComplete="off"
						type="password"
						value={newPassword ?? ''}
						error={!!newPasswordError}
						helperText={newPasswordError ?? ''}
						onChange={(e) => {
							setNewPassword(e.target.value);
							setNewPasswordError(null);
						}}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<AppCustomIcon tag="auth_password" sx={{ color: styleVariables.primaryColor }} />
								</InputAdornment>
							),
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							sx={{ minWidth: 160, fontWeight: 600 }}
							endIcon={<AppCustomIcon tag="arrow-right" />}
						>
							{t(langAuthOktaIdx.btn_save.tag)}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</form>
	);
};

export default FAuthOktaIdx;
